import useSWR from 'swr'
import { useAuthToken } from '@/hooks/useAuthToken'
import { needsAnalysisService } from '@/services/needsAnalysisService'
import type { NeedsAnalysisContextResponse } from '@/types/learner-portal'

/**
 * Hook to fetch needs analysis context data using SWR.
 * Returns the needs analysis record (if it exists) and contextual flags
 * like whether the user has a proficiency assessment.
 */
export function useNeedsAnalysis() {
  const { getToken, isSignedIn } = useAuthToken()

  const fetcher = async (url: string) => {
    const token = await getToken()
    if (!token) throw new Error('Authentication token not available')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to fetch needs analysis' }))
      throw new Error(error.detail || 'Failed to fetch needs analysis')
    }

    return response.json()
  }

  const { data, error, isLoading, mutate } = useSWR<NeedsAnalysisContextResponse>(
    isSignedIn ? needsAnalysisService.getStatusUrl() : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // 1 minute
    }
  )

  return {
    data: data?.needs_analysis ?? null,
    hasProficiencyAssessment: data?.has_proficiency_assessment ?? false,
    isLoading,
    error,
    refetch: mutate,
  }
}
